import * as React from 'react';
import * as Theme from '@sail/theme-classic';
import {view} from '../view';
import {useFieldContexts} from '../../components/hooks/useFieldContexts';
import {formFieldGenerator} from './FormField';
import {CompositeFieldContext} from '../../components/CompositeField';

export type NativeTextFieldProps = Pick<
  React.InputHTMLAttributes<HTMLInputElement>,
  | 'autoComplete'
  | 'autoFocus'
  | 'defaultValue'
  | 'disabled'
  | 'form'
  | 'inputMode'
  | 'maxLength'
  | 'minLength'
  | 'name'
  | 'onChange'
  | 'onKeyPress'
  | 'onKeyDown'
  | 'onKeyUp'
  | 'placeholder'
  | 'readOnly'
  | 'required'
  | 'spellCheck'
  | 'step'
  | 'tabIndex'
  | 'value'
> & {
  /**
   * Programmatically mark the value as invalid
   * @external
   */
  invalid?: boolean;
  /**
   * The size of the input
   * @external
   */
  size?: 'small' | 'medium' | 'large';
  /**
   * Choose between the text-alike types on an input
   * @external
   */
  type?: 'text' | 'email' | 'number' | 'password' | 'search' | 'tel' | 'url';
};

export const NativeTextField = view.input.create<NativeTextFieldProps>(
  ({size = 'medium', type = 'text', ...props}, View) => {
    // if these are part of props, pull them off so they don't override the values from context
    const {invalid: _, disabled: __, ...restProps} = props;

    const {id, invalid, ariaDescribedBy, ariaLabelledBy, disabled} =
      useFieldContexts(props);

    const contextValue = React.useContext(CompositeFieldContext);
    const isInCompositeField = contextValue?.isInCompositeField;

    return (
      <View
        type={type}
        id={id}
        aria-invalid={invalid}
        aria-describedby={ariaDescribedBy}
        aria-labelledby={ariaLabelledBy}
        disabled={disabled}
        uses={[
          restProps,
          Theme.TextFieldVariants.assign({
            invalid,
            size,
            isInCompositeField,
          }),
          Theme.focus,
          Theme.TextField,
        ]}
      />
    );
  },
  [Theme.TextFieldVariants.internal_defaults],
);

/**
 * @external
 * @param autoComplete
 * @param autoFocus
 * @param defaultValue
 * @param disabled
 * @param form
 * @param maxLength
 * @param minLength
 * @param name
 * @param onChange
 * @param onKeyPress
 * @param onKeyDown
 * @param onKeyUp
 * @param placeholder
 * @param readOnly
 * @param required
 * @param spellCheck
 * @param tabIndex
 * @param type
 */
export const TextField =
  formFieldGenerator<NativeTextFieldProps>(NativeTextField);
